import React from 'react';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
  ZAxis,
  Treemap,
} from 'recharts';
import { PageTitle } from 'ui/PageTitle';
import { theme } from '../../../tailwind.config';
import {
  trendData,
  statusData,
  PIE_COLORS,
  histogramData,
  boxPlotData,
  arrivalData,
  heatmapData,
  days,
  getHeatmapColor,
  hotelScatterData,
  salesByCountryData,
  churnRateData,
  churnByCountryData,
  monthsSinceBookingData,
  conversionRateData,
  salesRepScatterData,
  bookingsGuestsData,
  treemapData,
  salesBookingsByMonthData,
} from './mockData';

// Custom label for pie chart
const renderPieLabel = (entry: any) => {
  return entry.value;
};

// Custom tooltip for Sales and Bookings by Month chart
const SalesBookingsTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: theme.colors['white'],
          border: `1px solid ${theme.colors['gray-40']}`,
          padding: '10px 14px',
          fontFamily: 'PT Sans',
        }}
      >
        <p className="font-pt-sans text-13px font-bold text-black m-0 mb-8px">{label}</p>
        <p className="font-pt-sans text-12px m-0 mb-4px" style={{ color: '#E6C846' }}>
          Sales Current Year: <span style={{ color: theme.colors['black'] }}>{payload[0]?.value}</span>
        </p>
        <p className="font-pt-sans text-12px m-0 mb-4px" style={{ color: '#E8A54B' }}>
          Sales Previous Year: <span style={{ color: theme.colors['black'] }}>{payload[1]?.value}</span>
        </p>
        <p className="font-pt-sans text-12px m-0 mb-4px" style={{ color: '#4CAF50' }}>
          Bookings Current Year: <span style={{ color: theme.colors['black'] }}>{payload[2]?.value}</span>
        </p>
        <p className="font-pt-sans text-12px m-0" style={{ color: '#5B9BD5' }}>
          Bookings Previous Year: <span style={{ color: theme.colors['black'] }}>{payload[3]?.value}</span>
        </p>
      </div>
    );
  }
  return null;
};

// Custom content for treemap
const CustomTreemapContent = (props: any) => {
  const { x, y, width, height, name, days } = props;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: days > 15 ? theme.colors['yellow-30'] : days > 10 ? theme.colors['booking-com-sky-blue'] : theme.colors['green-90'],
          stroke: theme.colors['white'],
          strokeWidth: 2,
        }}
      />
      {width > 50 && height > 30 && (
        <>
          <text x={x + width / 2} y={y + height / 2 - 5} textAnchor="middle" fill={theme.colors['black']} fontSize={11}>
            {name}
          </text>
          <text x={x + width / 2} y={y + height / 2 + 10} textAnchor="middle" fill={theme.colors['black']} fontSize={10}>
            {days} days
          </text>
        </>
      )}
    </g>
  );
};

export const RechartsDemo: React.FC = () => {
  return (
    <div className="w-auto mx-20px crm-desktop:mx-80px mb-40px">
      <PageTitle primaryText="Recharts Demo" />

      <div className="flex flex-col gap-40px mt-30px">
        {/* Chart 1: Combined Chart - Bookings, Sales, Purchase, and Profit Trend */}
        <div className="bg-white p-20px rounded shadow-sm">
          <h2 className="font-pt-sans font-bold text-17px text-text-colour mb-20px m-0">
            Bookings, total sales, purchase and profit trend by creation date
          </h2>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart
              data={trendData}
              margin={{
                top: 20,
                right: 30,
                bottom: 20,
                left: 20,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.colors['gray-17']} />
              <XAxis dataKey="month" tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }} />
              <YAxis
                yAxisId="left"
                label={{ value: '#Bookings', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                label={{ value: 'Amount ($)', angle: 90, position: 'insideRight', style: { fontSize: 12 } }}
                tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.colors['white'],
                  border: `1px solid ${theme.colors['light-gray']}`,
                  borderRadius: '4px',
                  fontFamily: 'PT Sans',
                }}
              />
              {/* <Legend
                wrapperStyle={{ fontFamily: 'PT Sans', fontSize: 14 }}
                iconType="square"
                verticalAlign="bottom"
              /> */}
              <Bar yAxisId="left" dataKey="bookings" fill={theme.colors['booking-com-sky-blue']} name="Number of Bookings" />
              <Bar yAxisId="right" dataKey="purchase" fill={theme.colors['bright-sky-blue']} name="Purchase" />
              <Bar yAxisId="right" dataKey="sales" fill={theme.colors['green-90']} name="Sales" />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="profitPercent"
                stroke={theme.colors['yellow-30']}
                strokeWidth={3}
                name="Profit %"
                dot={{ fill: theme.colors['yellow-30'], r: 5 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-20px mt-10px justify-center flex-wrap">
            <div className="flex items-center gap-5px">
              <div className="w-15px h-15px bg-bright-sky-blue" />
              <p className="font-pt-sans text-13px text-text-colour m-0">Purchase</p>
            </div>
            <div className="flex items-center gap-5px">
              <div className="w-15px h-15px bg-booking-com-sky-blue" />
              <p className="font-pt-sans text-13px text-text-colour m-0">Number of Bookings</p>
            </div>
            <div className="flex items-center gap-5px">
              <div className="w-15px h-15px bg-green-90" />
              <p className="font-pt-sans text-13px text-text-colour m-0">Sales</p>
            </div>
            <div className="flex items-center gap-5px">
              <div className="w-15px h-15px bg-yellow-30" />
              <p className="font-pt-sans text-13px text-text-colour m-0">Profit%</p>
            </div>
          </div>
        </div>

        {/* Row with Pie Chart and Histogram */}
        <div className="grid grid-cols-1 crm-desktop:grid-cols-2 gap-20px">
          {/* Chart 2: Pie Chart - Number of Bookings by Status */}
          <div className="bg-white p-20px rounded shadow-sm">
            <h2 className="font-pt-sans font-bold text-17px text-text-colour mb-20px m-0">
              Number of Bookings by Status
            </h2>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderPieLabel}
                  outerRadius={120}
                  fill={theme.colors['purple']}
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.colors['white'],
                    border: `1px solid ${theme.colors['light-gray']}`,
                    borderRadius: '4px',
                    fontFamily: 'PT Sans',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-col gap-10px mt-20px">
              {statusData.map((entry, index) => (
                <div key={entry.name} className="flex items-center gap-10px">
                  <div className={`w-15px h-15px`} style={{ backgroundColor: PIE_COLORS[index] }} />
                  <p className="font-pt-sans text-13px text-text-colour m-0">{entry.name}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Chart 3: Histogram - Number of Bookings vs. Number of Guests */}
          <div className="bg-white p-20px rounded shadow-sm">
            <h2 className="font-pt-sans font-bold text-17px text-text-colour mb-20px m-0">
              Histogram Number of Bookings vs. Number of Guests
            </h2>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={histogramData}
                margin={{
                  top: 20,
                  right: 30,
                  bottom: 20,
                  left: 20,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors['gray-17']} />
                <XAxis
                  dataKey="guests"
                  label={{ value: '#Guests', position: 'insideBottom', offset: -10, style: { fontSize: 12 } }}
                  tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
                />
                <YAxis
                  label={{ value: '#Bookings', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                  tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.colors['white'],
                    border: `1px solid ${theme.colors['light-gray']}`,
                    borderRadius: '4px',
                    fontFamily: 'PT Sans',
                  }}
                />
                <Bar dataKey="bookings" fill={theme.colors['booking-com-navy-blue']} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Row with Box Plot and Area Chart */}
        <div className="grid grid-cols-1 crm-desktop:grid-cols-2 gap-20px">
          {/* Chart 4: Box Plot - Average Days Between Creation and Arrival */}
          <div className="bg-white p-20px rounded shadow-sm">
            <h2 className="font-pt-sans font-bold text-17px text-text-colour mb-20px m-0">
              Boxplot Average of Days Between the Creation and the Arrival
            </h2>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={boxPlotData}
                margin={{
                  top: 20,
                  right: 30,
                  bottom: 20,
                  left: 20,
                }}
                layout="horizontal"
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors['gray-17']} />
                <XAxis type="category" dataKey="category" tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }} />
                <YAxis
                  type="number"
                  label={{
                    value: 'Avg. Days Creation-Arrival',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 11 },
                  }}
                  tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.colors['white'],
                    border: `1px solid ${theme.colors['light-gray']}`,
                    borderRadius: '4px',
                    fontFamily: 'PT Sans',
                  }}
                />
                <Bar dataKey="value" fill={theme.colors['booking-com-sky-blue']} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Chart 5: Area Chart - Number of Bookings by Arrival Date */}
          <div className="bg-white p-20px rounded shadow-sm">
            <h2 className="font-pt-sans font-bold text-17px text-text-colour mb-20px m-0">
              Number of Bookings by Arrival Date (Press to filter)
            </h2>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart
                data={arrivalData}
                margin={{
                  top: 20,
                  right: 30,
                  bottom: 20,
                  left: 20,
                }}
              >
                <defs>
                  <linearGradient id="colorBookings" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={theme.colors['gray-100']} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={theme.colors['gray-100']} stopOpacity={0.3} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors['gray-17']} />
                <XAxis dataKey="date" tick={{ fill: theme.colors['gray-120'], fontSize: 11, fontFamily: 'PT Sans' }} />
                <YAxis
                  tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
                  domain={[0, 150]}
                  ticks={[0, 50, 100, 150]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.colors['white'],
                    border: `1px solid ${theme.colors['light-gray']}`,
                    borderRadius: '4px',
                    fontFamily: 'PT Sans',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="bookings"
                  stroke={theme.colors['flint']}
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorBookings)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 6: Heatmap - Audience by Time */}
        <div className="bg-white p-20px rounded shadow-sm">
          <h2 className="font-pt-sans font-bold text-17px text-text-colour mb-20px m-0">
            Last 7 Days Audience by Time (+01:00 UTC)
          </h2>
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full">
              <div className="flex">
                <div className="w-80px flex-shrink-0" />
                {days.map(day => (
                  <div key={day} className="flex-1 min-w-[100px] text-center">
                    <p className="font-pt-sans text-11px font-bold text-text-colour m-0 p-5px">{day}</p>
                  </div>
                ))}
              </div>
              {heatmapData.map((row, rowIndex) => (
                <div key={rowIndex} className="flex">
                  <div className="w-80px flex-shrink-0 flex items-center">
                    <p className="font-pt-sans text-10px text-text-colour m-0 p-5px">{row.time}</p>
                  </div>
                  {days.map(day => {
                    const value = row[day];
                    const color = getHeatmapColor(value);
                    return (
                      <div
                        key={day}
                        className="flex-1 min-w-[100px] flex items-center justify-center p-8px border border-gray-10"
                        style={{ backgroundColor: color }}
                      >
                        <p className="font-pt-sans text-11px font-bold text-white m-0">
                          {value.toLocaleString()}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-10px mt-15px">
            <p className="font-pt-sans text-11px text-text-colour m-0">Audience</p>
            <div className="flex items-center gap-2px">
              <div className="w-30px h-15px bg-purple" />
              <div className="w-30px h-15px bg-purple" />
              <div className="w-30px h-15px bg-purple-light" />
              <div className="w-30px h-15px bg-brown-60" />
              <div className="w-30px h-15px bg-brown-100" />
              <div className="w-30px h-15px bg-brown-hard" />
            </div>
            <p className="font-pt-sans text-11px text-text-colour m-0">533</p>
            <p className="font-pt-sans text-11px text-text-colour m-0">1,546</p>
          </div>
        </div>

        {/* Chart 7: Scatter Plot - Hotels by Score and Reviews */}
        <div className="bg-white p-20px rounded shadow-sm">
          <h2 className="font-pt-sans font-bold text-17px text-text-colour mb-20px m-0">
            Hotels by General Score and Number of Reviews
          </h2>
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart
              margin={{
                top: 20,
                right: 30,
                bottom: 20,
                left: 20,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.colors['gray-17']} />
              <XAxis
                type="number"
                dataKey="reviews"
                name="Number of Reviews"
                label={{ value: 'Number Of Reviews', position: 'insideBottom', offset: -10, style: { fontSize: 12 } }}
                tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
              />
              <YAxis
                type="number"
                dataKey="score"
                name="General Score"
                domain={[0, 5]}
                label={{ value: 'General Score', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
              />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                contentStyle={{
                  backgroundColor: theme.colors['white'],
                  border: `1px solid ${theme.colors['light-gray']}`,
                  borderRadius: '4px',
                  fontFamily: 'PT Sans',
                }}
              />
              <Scatter data={hotelScatterData} fill={theme.colors['green-90']} shape="star" />
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        {/* Chart 8: Stacked Area Chart - Sales by Country */}
        <div className="bg-white p-20px rounded shadow-sm">
          <h2 className="font-pt-sans font-bold text-17px text-text-colour mb-20px m-0">
            Monthly Trend: Sales and Profit from Commissionable Bookings by Arrival Date and Country
          </h2>
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart
              data={salesByCountryData}
              margin={{
                top: 20,
                right: 30,
                bottom: 20,
                left: 20,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.colors['gray-17']} />
              <XAxis dataKey="month" tick={{ fill: theme.colors['gray-120'], fontSize: 11, fontFamily: 'PT Sans' }} />
              <YAxis tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.colors['white'],
                  border: `1px solid ${theme.colors['light-gray']}`,
                  borderRadius: '4px',
                  fontFamily: 'PT Sans',
                }}
              />
              <Legend wrapperStyle={{ fontFamily: 'PT Sans', fontSize: 14 }} />
              <Area type="monotone" dataKey="Oman" stackId="1" stroke={theme.colors['status-requested']} fill={theme.colors['status-requested']} />
              <Area type="monotone" dataKey="UAE" stackId="1" stroke={theme.colors['booking-com-sky-blue']} fill={theme.colors['booking-com-sky-blue']} />
              <Area type="monotone" dataKey="Seychelles" stackId="1" stroke={theme.colors['green-90']} fill={theme.colors['green-90']} />
              <Area type="monotone" dataKey="SouthAfrica" stackId="1" stroke={theme.colors['purple-light']} fill={theme.colors['purple-light']} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Chart 9: Churn Rate Combined Chart */}
        <div className="bg-white p-20px rounded shadow-sm">
          <h2 className="font-pt-sans font-bold text-17px text-text-colour mb-20px m-0">
            Churn Rate & Balance of Travel Partner Trend
          </h2>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart
              data={churnRateData}
              margin={{
                top: 20,
                right: 30,
                bottom: 20,
                left: 20,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.colors['gray-17']} />
              <XAxis dataKey="month" tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }} />
              <YAxis
                yAxisId="left"
                label={{ value: 'Number of Partners', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                label={{ value: 'Churn Rate %', angle: 90, position: 'insideRight', style: { fontSize: 12 } }}
                tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.colors['white'],
                  border: `1px solid ${theme.colors['light-gray']}`,
                  borderRadius: '4px',
                  fontFamily: 'PT Sans',
                }}
              />
              <Legend wrapperStyle={{ fontFamily: 'PT Sans', fontSize: 14 }} />
              <Bar yAxisId="left" dataKey="activePartners" fill={theme.colors['green-90']} name="Active Partners" />
              <Bar yAxisId="left" dataKey="churnedPartners" fill={theme.colors['gray-100']} name="Churned Partners" />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="churnRate"
                stroke={theme.colors['status-confirmed']}
                strokeWidth={2}
                name="Churn Rate %"
                dot={{ fill: theme.colors['status-confirmed'], r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Row with Bubble Chart and Horizontal Bar */}
        <div className="grid grid-cols-1 crm-desktop:grid-cols-2 gap-20px">
          {/* Chart 10: Bubble Chart - Churn by Country */}
          <div className="bg-white p-20px rounded shadow-sm">
            <h2 className="font-pt-sans font-bold text-17px text-text-colour mb-20px m-0">
              Number of Churn Travel Partners by Country
            </h2>
            <div className="flex flex-col crm-desktop:flex-row gap-20px">
              {/* Legend */}
              <div className="flex-shrink-0 crm-desktop:w-[150px]">
                <p className="font-pt-sans text-11px text-gray-60 m-0 mb-10px">Travel partner Country</p>
                <div className="flex flex-row crm-desktop:flex-col gap-5px overflow-x-auto crm-desktop:overflow-y-auto crm-desktop:overflow-x-visible pb-10px crm-desktop:pb-0" style={{ maxHeight: '360px' }}>
                  {churnByCountryData.map((entry, index) => (
                    <div key={index} className="flex items-center gap-5px flex-shrink-0">
                      <div className="w-12px h-12px flex-shrink-0" style={{ backgroundColor: entry.color }} />
                      <p className="font-pt-sans text-10px text-text-colour m-0 whitespace-nowrap">{entry.country}</p>
                    </div>
                  ))}
                </div>
              </div>
              {/* Chart */}
              <div className="flex-1 min-w-0">
                <ResponsiveContainer width="100%" height={400}>
                  <ScatterChart
                    margin={{
                      top: 20,
                      right: 30,
                      bottom: 20,
                      left: 20,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.colors['gray-17']} />
                    <XAxis
                      type="number"
                      dataKey="x"
                      tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
                      domain={[0, 100]}
                    />
                    <YAxis
                      type="number"
                      dataKey="y"
                      tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
                      domain={[0, 100]}
                    />
                    <ZAxis type="number" dataKey="value" range={[100, 3000]} />
                    <Tooltip
                      cursor={{ strokeDasharray: '3 3' }}
                      contentStyle={{
                        backgroundColor: theme.colors['white'],
                        border: `1px solid ${theme.colors['light-gray']}`,
                        borderRadius: '4px',
                        fontFamily: 'PT Sans',
                      }}
                      content={({ payload }) => {
                        if (payload && payload.length > 0) {
                          const data = payload[0].payload;
                          return (
                            <div
                              style={{
                                backgroundColor: theme.colors['white'],
                                border: `1px solid ${theme.colors['light-gray']}`,
                                padding: '10px',
                                borderRadius: '4px',
                              }}
                            >
                              <p className="font-pt-sans text-12px m-0">
                                <strong>{data.country}</strong>
                              </p>
                              <p className="font-pt-sans text-11px m-0">Churned: {data.value}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Scatter data={churnByCountryData}>
                      {churnByCountryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Chart 11: Horizontal Bar - Months Since Last Booking */}
          <div className="bg-white p-20px rounded shadow-sm">
            <h2 className="font-pt-sans font-bold text-17px text-text-colour mb-20px m-0">
              Months since Last Booking of Churned Travel Partners (Size Month, color country)
            </h2>
            <div className="overflow-y-auto" style={{ height: '600px', paddingRight: '8px' }}>
              <div style={{ height: `${monthsSinceBookingData.length * 30}px`, minHeight: '600px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={monthsSinceBookingData}
                    layout="vertical"
                    margin={{
                      top: 10,
                      right: 20,
                      bottom: 10,
                      left: 20,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.colors['gray-17']} />
                    <XAxis
                      type="number"
                      tick={{ fill: theme.colors['gray-120'], fontSize: 11, fontFamily: 'PT Sans' }}
                      label={{ value: 'Months', position: 'insideBottom', offset: -5, style: { fontSize: 11 } }}
                    />
                    <YAxis
                      type="category"
                      dataKey="company"
                      tick={{ fill: theme.colors['gray-120'], fontSize: 9, fontFamily: 'PT Sans' }}
                      width={140}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: theme.colors['white'],
                        border: `1px solid ${theme.colors['light-gray']}`,
                        borderRadius: '4px',
                        fontFamily: 'PT Sans',
                      }}
                      content={({ payload }) => {
                        if (payload && payload.length > 0) {
                          const data = payload[0].payload;
                          return (
                            <div
                              style={{
                                backgroundColor: theme.colors['white'],
                                border: `1px solid ${theme.colors['light-gray']}`,
                                padding: '10px',
                                borderRadius: '4px',
                              }}
                            >
                              <p className="font-pt-sans text-12px m-0">
                                <strong>{data.company}</strong>
                              </p>
                              <p className="font-pt-sans text-11px m-0">Country: {data.country}</p>
                              <p className="font-pt-sans text-11px m-0">Months: {data.months}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="months">
                      {monthsSinceBookingData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Chart 12: Horizontal Stacked Bar - Conversion Rate */}
        <div className="bg-white p-20px rounded shadow-sm">
          <h2 className="font-pt-sans font-bold text-17px text-text-colour mb-20px m-0">
            Conversion Rate by Sales Rep. & Percentile Groups of Enquiries
          </h2>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={conversionRateData}
              layout="vertical"
              margin={{
                top: 10,
                right: 30,
                bottom: 10,
                left: 150,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.colors['gray-17']} />
              <XAxis
                type="number"
                tick={{ fill: theme.colors['gray-120'], fontSize: 11, fontFamily: 'PT Sans' }}
                label={{ value: 'Percentage %', position: 'insideBottom', offset: -5, style: { fontSize: 11 } }}
              />
              <YAxis type="category" dataKey="rep" tick={{ fill: theme.colors['gray-120'], fontSize: 10, fontFamily: 'PT Sans' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.colors['white'],
                  border: `1px solid ${theme.colors['light-gray']}`,
                  borderRadius: '4px',
                  fontFamily: 'PT Sans',
                }}
              />
              <Legend wrapperStyle={{ fontFamily: 'PT Sans', fontSize: 13 }} />
              <Bar dataKey="low" stackId="a" fill={theme.colors['gray-80']} name="Low Percentile" />
              <Bar dataKey="medium" stackId="a" fill={theme.colors['bright-sky-blue']} name="Medium Percentile" />
              <Bar dataKey="high" stackId="a" fill={theme.colors['green-90']} name="High Percentile" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Chart 13: Scatter Plot - Sales Rep Performance */}
        <div className="bg-gray-10 p-20px rounded shadow-sm">
          <h2 className="font-pt-sans font-bold text-17px text-text-colour mb-20px m-0">
            Sales Rep. Percentile Groups of Enquiries & confirmed Bookings
          </h2>
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart
              margin={{
                top: 20,
                right: 30,
                bottom: 20,
                left: 20,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.colors['gray-17']} />
              <XAxis
                type="number"
                dataKey="enquiries"
                name="Enquiries"
                label={{ value: 'Enquiries', position: 'insideBottom', offset: -10, style: { fontSize: 12 } }}
                tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
              />
              <YAxis
                type="number"
                dataKey="bookings"
                name="Confirmed Bookings"
                label={{ value: 'Confirmed', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
              />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                contentStyle={{
                  backgroundColor: theme.colors['white'],
                  border: `1px solid ${theme.colors['light-gray']}`,
                  borderRadius: '4px',
                  fontFamily: 'PT Sans',
                }}
              />
              <Scatter data={salesRepScatterData} fill={theme.colors['green-100']} />
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        {/* Chart 14: Grouped Horizontal Bar - Bookings and Guests */}
        <div className="bg-white p-20px rounded shadow-sm">
          <h2 className="font-pt-sans font-bold text-17px text-text-colour mb-20px m-0">
            Number of Bookings & Guests by Hotel
          </h2>
          <ResponsiveContainer width="100%" height={450}>
            <BarChart
              data={bookingsGuestsData}
              layout="vertical"
              margin={{
                top: 10,
                right: 30,
                bottom: 10,
                left: 150,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.colors['gray-17']} />
              <XAxis type="number" tick={{ fill: theme.colors['gray-120'], fontSize: 11, fontFamily: 'PT Sans' }} />
              <YAxis type="category" dataKey="hotel" tick={{ fill: theme.colors['gray-120'], fontSize: 10, fontFamily: 'PT Sans' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.colors['white'],
                  border: `1px solid ${theme.colors['light-gray']}`,
                  borderRadius: '4px',
                  fontFamily: 'PT Sans',
                }}
              />
              <Legend wrapperStyle={{ fontFamily: 'PT Sans', fontSize: 13 }} />
              <Bar dataKey="bookings" fill={theme.colors['booking-com-navy-blue']} name="Number of Bookings" />
              <Bar dataKey="guests" fill={theme.colors['green-90']} name="Number of Guests" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Chart 15: Treemap - Average Days of Stay */}
        <div className="bg-white p-20px rounded shadow-sm">
          <h2 className="font-pt-sans font-bold text-17px text-text-colour mb-20px m-0">
            Average Days of Stay Treemap by Country & Hotel
          </h2>
          <ResponsiveContainer width="100%" height={500}>
            <Treemap
              data={treemapData}
              dataKey="size"
              stroke={theme.colors['white']}
              fill={theme.colors['purple']}
              content={<CustomTreemapContent />}
            />
          </ResponsiveContainer>
          <div className="flex items-center gap-20px mt-15px flex-wrap">
            <div className="flex items-center gap-5px">
              <div className="w-20px h-15px bg-green-90" />
              <p className="font-pt-sans text-12px text-text-colour m-0">UAE (6-9 days)</p>
            </div>
            <div className="flex items-center gap-5px">
              <div className="w-20px h-15px bg-booking-com-sky-blue" />
              <p className="font-pt-sans text-12px text-text-colour m-0">Seychelles (10-15 days)</p>
            </div>
            <div className="flex items-center gap-5px">
              <div className="w-20px h-15px bg-yellow-30" />
              <p className="font-pt-sans text-12px text-text-colour m-0">South Africa (15+ days)</p>
            </div>
          </div>
        </div>

        {/* Chart 16: Currently Active Leads Pipeline */}
        <div className="bg-white p-20px rounded shadow-sm">
          <h2 className="font-pt-sans font-bold text-17px text-text-colour mb-20px ml-10 m-0">
            Currently Active Leads
          </h2>
          <div className="ml-10 mb-20px">
            <p className="font-pt-sans text-40px font-bold text-text-colour m-0">{141}</p>
          </div>
          <div className="flex flex-row-reverse justify-center items-center w-full" style={{ height: '120px' }}>
            {/* Confirmed Stage */}
            <div
              className="relative flex flex-col items-center justify-center text-white"
              style={{
                flex: '0 0 33%',
                height: '100%',
                backgroundColor: '#10B981',
                marginLeft: '-20px',
                borderRadius: '0 8px 8px 0',
                clipPath: 'polygon(20px 0, 100% 0, 100% 100%, 20px 100%)',
              }}
            >
              <p className="font-pt-sans text-14px font-bold m-0 mb-5px">Confirmed</p>
              <p className="font-pt-sans text-32px font-bold m-0">31</p>
            </div>
            {/* Nurture Stage */}
            <div
              className="relative flex flex-col items-center justify-center text-white mr-[-20px]"
              style={{
                flex: '0 0 33%',
                height: '100%',
                backgroundColor: '#8B5CF6',
                marginLeft: '-20px',
                clipPath: 'polygon(20px 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 20px 100%)',
              }}
            >
              <p className="font-pt-sans text-14px font-bold m-0 mb-5px">Nurture</p>
              <p className="font-pt-sans text-32px font-bold m-0">68</p>
            </div>
            {/* Discover Stage */}
            <div
              className="relative flex flex-col items-center justify-center text-white mr-[-20px]"
              style={{
                flex: '0 0 34%',
                height: '100%',
                backgroundColor: '#F59E0B',
                borderRadius: '8px 0 0 8px',
                clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%)',
              }}
            >
              <p className="font-pt-sans text-14px font-bold m-0 mb-5px">Discover</p>
              <p className="font-pt-sans text-32px font-bold m-0">42</p>
            </div>
          </div>
        </div>

        {/* Chart 17: Win Rate and Loss Rate */}
        <div className="bg-white p-20px rounded shadow-sm max-w-[600px] m-auto">
          <div className="flex justify-between items-center gap-80px">
            {/* Win Rate Section */}
            <div className="flex flex-col">
              <p className="font-pt-sans text-14px text-text-colour m-0 mb-10px">Win Rate:</p>
              <p className="font-pt-sans text-48px font-bold m-0 mb-5px" style={{ color: '#808000' }}>
                22.4%
              </p>
              <p className="font-pt-sans text-13px text-text-colour m-0">99 of 441 converted</p>
            </div>
            {/* Loss Rate Section */}
            <div className="flex flex-col">
              <p className="font-pt-sans text-14px text-text-colour m-0 mb-10px">Loss Rate:</p>
              <p className="font-pt-sans text-48px font-bold m-0 mb-5px" style={{ color: '#92400E' }}>
                77.6%
              </p>
              <p className="font-pt-sans text-13px text-text-colour m-0">342 discarded</p>
            </div>
          </div>
          <div className="flex justify-center items-center mt-[30px]">
            <p className="font-pt-sans text-14px text-text-colour m-0">Total Cases: 441</p>
          </div>
        </div>

        {/* Chart 18: Sales Pipeline Funnel */}
        <div className="bg-white p-20px rounded shadow-sm">
          <div className="flex items-center justify-center gap-8px mb-20px">
            <h2 className="font-pt-sans font-bold text-17px text-text-colour m-0">Sales Pipeline</h2>
            <span
              aria-hidden="true"
              className="inline-flex items-center justify-center rounded-full"
              style={{
                width: '16px',
                height: '16px',
                border: `1px solid ${theme.colors['gray-60']}`,
                color: theme.colors['gray-120'],
                fontSize: '11px',
                lineHeight: 1,
              }}
            >
              i
            </span>
          </div>

          {(() => {
            const stages = [
              { label: 'Discover', count: 121, percent: '27.4%', color: '#6F8EF6' },
              { label: 'Nurture', count: 110, percent: '25.0%', color: '#D0A24A' },
              { label: 'Confirmed', count: 107, percent: '24.3%', color: '#86B77A' },
              { label: 'Converted', count: 103, percent: '23.3%', color: '#7D736C' },
            ] as const;

            const svgW = 280;
            const svgH = 320;
            const cx = svgW / 2;
            const segH = 70;
            // Top-to-bottom widths (last stage tapers to a point)
            const widths = [260, 200, 120, 60, 0];

            return (
              <div className="flex flex-col crm-desktop:flex-row items-center justify-center gap-20px">
                {/* Funnel */}
                <div className="flex-shrink-0">
                  <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
                    {stages.map((s, i) => {
                      const yTop = i * segH;
                      const yBot = yTop + segH;
                      const topW = widths[i];
                      const botW = widths[i + 1];
                      const lt = cx - topW / 2;
                      const rt = cx + topW / 2;
                      const lb = cx - botW / 2;
                      const rb = cx + botW / 2;
                      const points = `${lt},${yTop} ${rt},${yTop} ${rb},${yBot} ${lb},${yBot}`;
                      const y = botW === 0 ? yTop + segH / 2 - 15 : yTop + segH / 2;

                      return (
                        <g key={s.label}>
                          <polygon points={points} fill={s.color} />
                          <text
                            x={cx}
                            y={y}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill={theme.colors.white}
                            style={{ fontFamily: 'PT Sans', fontSize: '12px', fontWeight: 700 }}
                          >
                            {s.percent}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                </div>

                {/* Legend (number + swatch + label) */}
                <div className="flex flex-col gap-18px">
                  {stages.map(s => (
                    <div key={s.label} className="flex items-center" style={{ gap: '10px' }}>
                      <p className="font-pt-sans text-14px font-bold text-gray-120 m-0" style={{ width: '34px', textAlign: 'right' }}>
                        {s.count}
                      </p>
                      <div className="w-14px h-14px rounded-sm" style={{ backgroundColor: s.color }} />
                      <p className="font-pt-sans text-14px text-gray-120 m-0">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Chart 19: Sales and Bookings by Month (Current vs Previous Year) */}
        <div className="bg-white p-20px rounded shadow-sm">
          <h2 className="font-pt-sans font-bold text-15px text-gray-100 mb-20px m-0 text-center tracking-wide uppercase">
            Sales and Bookings by Month
          </h2>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart
              data={salesBookingsByMonthData}
              margin={{
                top: 20,
                right: 60,
                bottom: 20,
                left: 20,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.colors['gray-17']} vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
                axisLine={{ stroke: theme.colors['gray-40'] }}
                tickLine={false}
              />
              <YAxis
                yAxisId="left"
                label={{
                  value: 'Sales (€)',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 12, fontFamily: 'PT Sans', fill: theme.colors['gray-100'] },
                }}
                tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
                axisLine={{ stroke: theme.colors['gray-40'] }}
                tickLine={false}
                domain={[0, 160]}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                label={{
                  value: 'Bookings',
                  angle: 90,
                  position: 'insideRight',
                  style: { fontSize: 12, fontFamily: 'PT Sans', fill: theme.colors['gray-100'] },
                }}
                tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
                axisLine={{ stroke: theme.colors['gray-40'] }}
                tickLine={false}
                domain={[0, 3.4]}
              />
              <Tooltip content={<SalesBookingsTooltip />} />
              <Bar yAxisId="left" dataKey="salesCurrentYear" fill="#E6C846" name="Sales Current Year" barSize={30} />
              <Bar yAxisId="left" dataKey="salesPreviousYear" fill="#E8A54B" name="Sales Previous Year" barSize={30} />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="bookingsCurrentYear"
                stroke="#4CAF50"
                strokeWidth={2}
                name="Bookings Current Year"
                dot={{ fill: '#4CAF50', r: 4, strokeWidth: 0 }}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="bookingsPreviousYear"
                stroke="#5B9BD5"
                strokeWidth={2}
                name="Bookings Previous Year"
                dot={{ fill: '#5B9BD5', r: 4, strokeWidth: 0 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
          {/* Custom legend */}
          <div className="flex items-center gap-30px mt-10px justify-center flex-wrap">
            <div className="flex items-center gap-8px">
              <div className="w-15px h-15px" style={{ backgroundColor: '#E6C846' }} />
              <p className="font-pt-sans text-13px m-0" style={{ color: theme.colors['black'] }}>
                Sales Current Year
              </p>
            </div>
            <div className="flex items-center gap-8px">
              <div className="w-15px h-15px" style={{ backgroundColor: '#E8A54B' }} />
              <p className="font-pt-sans text-13px m-0" style={{ color: theme.colors['black'] }}>
                Sales Previous Year
              </p>
            </div>
            <div className="flex items-center gap-8px">
              <div className="w-15px h-15px" style={{ backgroundColor: '#4CAF50' }} />
              <p className="font-pt-sans text-13px m-0" style={{ color: theme.colors['black'] }}>
                Bookings Current Year
              </p>
            </div>
            <div className="flex items-center gap-8px">
              <div className="w-15px h-15px" style={{ backgroundColor: '#5B9BD5' }} />
              <p className="font-pt-sans text-13px m-0" style={{ color: theme.colors['black'] }}>
                Bookings Previous Year
              </p>
            </div>
          </div>
          {/* Note */}
          <p className="font-pt-sans text-12px text-gray-100 m-0 mt-20px">
            <span className="underline">NOTE</span> All booking values are shown in USD. Bookings originally made in EUR
            are converted using a standard exchange rate.
          </p>
        </div>
      </div>
    </div>
  );
};
